<script setup>
import { ref, computed, defineProps, defineEmits, watch, onMounted, onUnmounted, nextTick } from 'vue';
import draggable from 'vuedraggable';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useStore } from 'vuex';
import { PlusIcon, PencilSquareIcon } from '@heroicons/vue/24/solid';
import TimeseriesWidget from '@/components/widgets/timeseries/TimeseriesWidget.vue';
import DatePicker from '@/components/dropdowns/DatePicker.vue';
import { hasPermission } from '@/tools/PermissionChecker';

const props = defineProps({
  dataID: {
    type: String,
    required: true
  },
  page: {
    type: Object,
    required: true
  },
  templateId: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  historicalData: {
    type: Object,
    default: () => ({})
  },
  historicalLoading: {
    type: Object,
    default: () => ({})
  },
  isLiveMode: {
    type: Boolean,
    default: true
  },
  isSseHealthy: {
    type: Boolean,
    default: true
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  },
  dashboardMode: {
    type: String,
    default: ''
  },
  showEditModeToggle: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['addChart', 'editChart', 'deleteChart', 'dateRangeChange']);

// Parse API timestamp as UTC (same logic as TimeseriesWidget)
function parseUTC(s) {
  if (!s) return null;
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(str) || str.endsWith('Z')) return new Date(str);
  return new Date(str.replace(' ', 'T') + 'Z');
}

const downloadCSV = () => {
  if (!chartsList.value || chartsList.value.length === 0) return;

  // Collect all series across all charts: { colHeader, source, field, unit }
  const series = [];
  chartsList.value.forEach(chart => {
    if (!chart.specifications) return;
    Object.entries(chart.specifications).forEach(([source, specs]) => {
      specs.forEach(spec => {
        const label = spec.friendlyName || spec.field;
        const unit = spec.unit ? ` (${spec.unit})` : '';
        series.push({
          colHeader: `${label}${unit}`,
          source,
          field: spec.field,
        });
      });
    });
  });

  if (series.length === 0) return;

  // Merge all data points by timestamp across all sources
  // Map: isoTimestamp -> { [colHeader]: value }
  const rowMap = new Map();

  const addRow = (isoTimestamp, colHeader, value) => {
    if (!rowMap.has(isoTimestamp)) rowMap.set(isoTimestamp, {});
    rowMap.get(isoTimestamp)[colHeader] = value;
  };

  // Collect from historical data
  series.forEach(({ source, field, colHeader }) => {
    const sourceData = props.historicalData?.[source] || [];
    sourceData.forEach(doc => {
      if (!doc.messageUTC || doc[field] === undefined) return;
      const ts = parseUTC(doc.messageUTC);
      if (!ts || Number.isNaN(ts.getTime())) return;
      let val = doc[field];
      if (typeof val === 'boolean') val = val ? 1 : 0;
      addRow(ts.toISOString(), colHeader, val);
    });
  });

  // Overlay streaming data (live mode adds points not in historical)
  if (props.isLiveMode) {
    series.forEach(({ source, field, colHeader }) => {
      const lastValue = props.streamData?.[source]?.lastValue;
      if (!lastValue || lastValue[field] === undefined || !lastValue.messageUTC) return;
      const ts = parseUTC(lastValue.messageUTC);
      if (!ts || Number.isNaN(ts.getTime())) return;
      let val = lastValue[field];
      if (typeof val === 'boolean') val = val ? 1 : 0;
      addRow(ts.toISOString(), colHeader, val);
    });
  }

  if (rowMap.size === 0) return;

  // Sort rows by timestamp ascending
  const sortedTimestamps = [...rowMap.keys()].sort();

  // Build CSV
  const headers = ['Timestamp', ...series.map(s => s.colHeader)];
  const escape = (v) => {
    const str = v == null ? '' : String(v);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const csvLines = [
    headers.map(escape).join(','),
    ...sortedTimestamps.map(ts => {
      const row = rowMap.get(ts);
      return [ts, ...series.map(s => row[s.colHeader] ?? '')].map(escape).join(',');
    })
  ];

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const rangeLabel = selectedDateRange.value?.length === 2
    ? `_${selectedDateRange.value[0]}_to_${selectedDateRange.value[1]}`.replace(/[^a-zA-Z0-9_-]/g, '-')
    : '_live';
  link.download = `timeseries${rangeLabel}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Date range state
const selectedDateRange = ref([]);
const pendingDateRange = ref([]); // Holds picker selection until user clicks "View History"
const datePickerRef = ref(null);

// Called by DatePicker on every valid selection - just stage it, don't fetch yet
const handleDateRangeChange = (range) => {
  pendingDateRange.value = range;
};

// Called when the user clicks "View History"
const handleViewHistory = () => {
  const range = pendingDateRange.value;
  if (!range || range.length !== 2 || !range[0] || !range[1]) return;
  datePickerRef.value?.close();
  selectedDateRange.value = range;
  const start = new Date(range[0]).getTime();
  const end = new Date(range[1]).getTime();
  const newSync = { range: [start, end], revision: Date.now() };
  syncedXAxis.value = newSync;
  fullXAxisRange.value = newSync;
  emit('dateRangeChange', { start: range[0], end: range[1] });
};

const handleGoLive = () => {
  selectedDateRange.value = [];
  pendingDateRange.value = [];
  const newSync = getDefaultXAxisRange();
  syncedXAxis.value = newSync;
  fullXAxisRange.value = newSync;
  emit('dateRangeChange', null);
};
const store = useStore();

const canEditDashboard = computed(() => hasPermission('editDashboard'));
const isParentControlledMode = computed(() => props.dashboardMode === 'view' || props.dashboardMode === 'edit');

// Default x-axis: last 24 hours (used so empty charts and all charts stay aligned)
const getDefaultXAxisRange = () => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return { range: [twentyFourHoursAgo.getTime(), now.getTime()], revision: 0 };
};

// State
const editMode = ref(false);
const saving = ref(false);
const syncedXAxis = ref(getDefaultXAxisRange()); // Shared x-axis range for all charts (default last 24h)
const fullXAxisRange = ref(getDefaultXAxisRange()); // The "full" (unzoomed) range - used by "Back to full view"
const showCrosshair = ref(false);
const crosshairPosition = ref(0); // Pixel position from left
const isDragging = ref(false); // Track if user is dragging the line
const crosshairContainerLeft = ref(0); // Left edge of container (for child widgets to convert pixel to data)
const crosshairLineRef = ref(null); // Ref to the crosshair line element so we can read its actual position
const crosshairViewportX = ref(0); // Actual viewport X of crosshair center (so tooltips match the line exactly)
const crosshairHeight = ref(0); // Full scroll height of charts list so the line extends entire height

// Computed
// Local copy of charts order - updated instantly on drop to avoid flash from async save round-trip
const localChartsList = ref(props.page.charts ? [...props.page.charts] : []);

// Keep local list in sync when the prop changes from outside (e.g. add/delete chart)
watch(() => props.page.charts, (newCharts) => {
  localChartsList.value = newCharts ? [...newCharts] : [];
}, { deep: true });

watch(() => props.dashboardMode, (mode) => {
  if (!isParentControlledMode.value) return;
  editMode.value = mode === 'edit';
}, { immediate: true });

const chartsList = computed({
  get: () => localChartsList.value,
  set: (newCharts) => {
    localChartsList.value = newCharts;
    saveChartsOrder(newCharts);
  }
});

// Methods
const saveChartsOrder = async (newCharts) => {
  saving.value = true;
  
  try {
    const templateRef = doc(db, 'templates', props.templateId);
    
    // Fetch fresh template to preserve other pages
    const templateDoc = await getDoc(templateRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }
    
    const templateData = templateDoc.data();
    
    // Update only our page with the new charts order
    const updatedPage = {
      ...props.page,
      charts: newCharts
    };
    
    const updatedPages = templateData.pages.map(p => 
      p.id === props.page.id ? updatedPage : p
    );
    
    await updateDoc(templateRef, { pages: updatedPages });
    
    // Update the store
    const updatedTemplateData = { 
      ...templateData, 
      pages: updatedPages,
      id: props.templateId,
      dataID: props.dataID
    };
    await store.dispatch('setTemplate', updatedTemplateData);
    
  } catch (err) {
    console.error('Error saving charts order:', err);
    alert('Failed to save charts order. Please try again.');
  } finally {
    saving.value = false;
  }
};

const handleAddChart = () => {
  editMode.value = true;
  emit('addChart');
};

const handleEditChart = (chartId) => {
  emit('editChart', chartId);
};

const handleDeleteChart = async (chartId) => {
  if (!confirm('Are you sure you want to delete this chart?')) return;
  
  saving.value = true;
  
  try {
    const templateRef = doc(db, 'templates', props.templateId);
    
    // Fetch fresh template
    const templateDoc = await getDoc(templateRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }
    
    const templateData = templateDoc.data();
    
    // Remove the chart from the page
    const updatedCharts = props.page.charts.filter(chart => chart.id !== chartId);
    
    const updatedPage = {
      ...props.page,
      charts: updatedCharts
    };
    
    const updatedPages = templateData.pages.map(p => 
      p.id === props.page.id ? updatedPage : p
    );
    
    await updateDoc(templateRef, { pages: updatedPages });
    
    // Update the store
    const updatedTemplateData = { 
      ...templateData, 
      pages: updatedPages,
      id: props.templateId,
      dataID: props.dataID
    };
    await store.dispatch('setTemplate', updatedTemplateData);
    
    emit('deleteChart', chartId);
  } catch (err) {
    console.error('Error deleting chart:', err);
    alert('Failed to delete chart. Please try again.');
  } finally {
    saving.value = false;
  }
};

const handleXAxisChange = (syncData) => {
  syncedXAxis.value = syncData;
  // Keep fullXAxisRange in sync after a full reset so other charts' userHasZoomed clears correctly
  if (syncData.isFullReset) {
    fullXAxisRange.value = syncData;
  }
};

const chartsContainer = ref(null);
const outerContainer = ref(null);

const startCrosshairDrag = () => {
  isDragging.value = true;

  let rafId = null;
  let pendingX = null;

  const onMove = (clientX) => {
    if (!chartsContainer.value) return;
    pendingX = clientX;
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (pendingX === null) return;
        const rect = chartsContainer.value.getBoundingClientRect();
        crosshairPosition.value = Math.max(0, Math.min(pendingX - rect.left, rect.width));
        crosshairViewportX.value = pendingX;
        pendingX = null;
      });
    }
  };

  const onEnd = (clientX) => {
    isDragging.value = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
    if (clientX != null && chartsContainer.value) {
      const rect = chartsContainer.value.getBoundingClientRect();
      crosshairPosition.value = Math.max(0, Math.min(clientX - rect.left, rect.width));
      crosshairViewportX.value = clientX;
    }
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  };

  const onMouseMove = (e) => onMove(e.clientX);
  const onMouseUp = (e) => onEnd(e.clientX);
  const onTouchMove = (e) => { e.preventDefault(); onMove(e.touches[0].clientX); };
  const onTouchEnd = (e) => onEnd(e.changedTouches[0]?.clientX ?? null);

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend', onTouchEnd);
};

const handleCrosshairMouseDown = (event) => {
  event.preventDefault();
  event.stopPropagation();
  startCrosshairDrag();
};

const handleCrosshairTouchStart = (event) => {
  event.stopPropagation();
  startCrosshairDrag();
};

const centerCrosshair = () => {
  if (!chartsContainer.value) return;
  const rect = chartsContainer.value.getBoundingClientRect();
  const centerX = rect.width / 2;
  crosshairPosition.value = centerX;
  crosshairViewportX.value = rect.left + centerX;
};

const updateCrosshairContainerRect = () => {
  if (chartsContainer.value && showCrosshair.value) {
    crosshairContainerLeft.value = chartsContainer.value.getBoundingClientRect().left;
    crosshairHeight.value = chartsContainer.value.scrollHeight;
  }
  // Use the crosshair line's actual position so tooltips match exactly
  if (crosshairLineRef.value && showCrosshair.value) {
    const rect = crosshairLineRef.value.getBoundingClientRect();
    crosshairViewportX.value = rect.left + rect.width / 2;
  }
};

watch(showCrosshair, (visible) => {
  if (visible) {
    centerCrosshair();
    updateCrosshairContainerRect();
    // Content (e.g. charts) may still be loading; recalc height after a short delay
    setTimeout(() => {
      centerCrosshair();
      updateCrosshairContainerRect();
    }, 400);
  }
});
watch(crosshairPosition, () => {
  if (showCrosshair.value) nextTick(updateCrosshairContainerRect);
});
watch(chartsList, () => {
  if (showCrosshair.value) nextTick(updateCrosshairContainerRect);
}, { deep: true });

// Find the nearest scrollable ancestor (the page-level <main> on mobile)
const getScrollParent = (el) => {
  if (!el) return window;
  const style = window.getComputedStyle(el);
  if (/(auto|scroll)/.test(style.overflow + style.overflowY)) return el;
  return getScrollParent(el.parentElement);
};

let scrollParent = null;

onMounted(() => {
  window.addEventListener('resize', updateCrosshairContainerRect);
  const container = chartsContainer.value;
  if (container) {
    container.addEventListener('scroll', updateCrosshairContainerRect);
    scrollParent = getScrollParent(container.parentElement);
    if (scrollParent && scrollParent !== container) {
      scrollParent.addEventListener('scroll', updateCrosshairContainerRect);
    }
  }
  if (showCrosshair.value) nextTick(updateCrosshairContainerRect);
});
onUnmounted(() => {
  window.removeEventListener('resize', updateCrosshairContainerRect);
  const container = chartsContainer.value;
  if (container) container.removeEventListener('scroll', updateCrosshairContainerRect);
  if (scrollParent && scrollParent !== chartsContainer.value) {
    scrollParent.removeEventListener('scroll', updateCrosshairContainerRect);
  }
});


</script>

<template>
  <div ref="outerContainer" class="w-full h-full min-h-0 max-h-full relative flex flex-col overflow-hidden">
    <!-- Toolbar -->
    <div class="flex-none flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4">
      <!-- Row 1 (mobile) / Left group (desktop): Add Chart + Date controls -->
      <div class="flex flex-wrap items-center gap-2 flex-1 min-w-0">
        <button
          v-if="editMode"
          @click="handleAddChart"
          class="btn-primary flex items-center gap-2 shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
          </svg>
          Add Chart
        </button>
        <div v-if="chartsList && chartsList.length > 0 && !editMode">
          <!-- Date Range Picker -->
          <div class="flex flex-wrap items-center gap-2 min-w-0">
            <div class="flex-1 sm:w-80 min-w-0">
              <DatePicker
                ref="datePickerRef"
                :modelValue="pendingDateRange"
                @update:modelValue="handleDateRangeChange"
              />
            </div>

            <!-- View History button: enabled only when a valid pending range is staged -->
            <button
              v-if="pendingDateRange && pendingDateRange.length === 2 && pendingDateRange[0] && pendingDateRange[1]"
              @click="handleViewHistory"
              class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded transition-colors shrink-0"
              :class="pendingDateRange && pendingDateRange.length === 2 && pendingDateRange[0] && pendingDateRange[1]
                ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'"
              title="Fetch data for selected time range"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="hidden sm:inline">View History</span>
              <span class="sm:hidden">History</span>
            </button>

            <!-- Live indicator / button -->
            <button
              v-if="!isLiveMode"
              @click="handleGoLive"
              class="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors shrink-0"
              title="Return to live view"
            >
              <span class="relative flex h-2 w-2">
                <span class="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              Go Live
            </button>
            <div
              v-else-if="isSseHealthy"
              class="flex items-center gap-1.5 px-3 py-2 text-gray-600 dark:text-gray-300 text-sm font-medium shrink-0"
            >
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              Live
            </div>
            <div
              v-else
              class="flex items-center gap-1.5 px-3 py-2 text-amber-700 dark:text-amber-300 text-sm font-medium shrink-0"
            >
              <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
              Live unavailable
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2 (mobile only): secondary tools — crosshair | CSV | edit layout -->
      <div
        v-if="chartsList && chartsList.length > 0 && !editMode"
        class="flex items-center gap-4 sm:hidden px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
      >
        <!-- Crosshair toggle (mobile copy) -->
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Crosshair</span>
          <div class="flex items-center">
            <input
              id="crosshairToggleMobile"
              type="checkbox"
              v-model="showCrosshair"
              class="toggle-checkbox hidden"
            />
            <label
              for="crosshairToggleMobile"
              class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative transition-colors"
              :class="showCrosshair ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
            >
              <span
                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                :class="showCrosshair ? 'translate-x-6' : ''"
              ></span>
            </label>
          </div>
        </div>

        <span class="text-gray-300 dark:text-gray-600 select-none">|</span>

        <!-- CSV download (mobile copy) -->
        <button
          @click="downloadCSV"
          class="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200"
          title="Download all chart data as CSV"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          CSV
        </button>

        <!-- Edit Layout toggle (mobile copy, only if permitted) -->
        <template v-if="canEditDashboard">
          <span class="text-gray-300 dark:text-gray-600 select-none">|</span>
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Edit</span>
            <div class="flex items-center">
              <input
                id="editModeToggleMobile"
                type="checkbox"
                v-model="editMode"
                class="toggle-checkbox hidden"
              />
              <label
                for="editModeToggleMobile"
                class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative transition-colors"
                :class="editMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
              >
                <span
                  class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                  :class="editMode ? 'translate-x-6' : ''"
                ></span>
              </label>
            </div>
          </div>
        </template>
      </div>

      <!-- Desktop only: Download CSV (hidden on mobile — appears in Row 2 instead) -->
      <button
        v-if="chartsList && chartsList.length > 0 && !editMode"
        @click="downloadCSV"
        class="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm shrink-0"
        title="Download all chart data as CSV"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        <span>Download CSV</span>
      </button>

      <!-- Desktop only: Crosshair + Edit Layout toggles (hidden on mobile — appear in Row 2 instead) -->
      <div class="hidden sm:flex flex-wrap items-center gap-3">
        <!-- Crosshair Toggle Switch -->
        <div v-if="chartsList && chartsList.length > 0 && !editMode" class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Crosshair</span>
          <div class="flex items-center">
            <input
              id="crosshairToggle"
              type="checkbox"
              v-model="showCrosshair"
              class="toggle-checkbox hidden"
            />
            <label
              for="crosshairToggle"
              class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative transition-colors"
              :class="showCrosshair ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
            >
              <span
                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                :class="showCrosshair ? 'translate-x-6' : ''"
              ></span>
            </label>
          </div>
        </div>

        <!-- Edit Mode Toggle Switch (admin or editDashboard permission only) -->
        <div v-if="canEditDashboard && showEditModeToggle" class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Edit Layout</span>
          <div class="flex items-center">
            <input
              id="editModeToggle"
              type="checkbox"
              v-model="editMode"
              class="toggle-checkbox hidden"
            />
            <label
              for="editModeToggle"
              class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative transition-colors"
              :class="editMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
            >
              <span
                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                :class="editMode ? 'translate-x-6' : ''"
              ></span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!chartsList || chartsList.length === 0" class="p-12 rounded-lg text-center">
      <div class="text-gray-500 dark:text-gray-400 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 class="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">No Time Series Yet</h3>
        <div v-if="canEditDashboard && editMode">
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            Start building your dashboard by adding time series charts
          </p>
          <button
            @click="handleAddChart"
            class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center gap-2 mx-auto transition-all shadow-lg"
          >
            <PlusIcon class="w-5 h-5" />
            Add Your First Chart
          </button>
        </div>
      </div>
    </div>

    <!-- Crosshair Handle - floats just above the charts container top -->
    <div
      v-if="showCrosshair"
      class="relative z-50 pointer-events-none h-0"
    >
      <div
        class="absolute pointer-events-auto flex items-center cursor-ew-resize select-none"
        :style="{ left: `${crosshairPosition}px`, transform: 'translateX(-50%) translateY(-100%)' }"
        @mousedown="handleCrosshairMouseDown"
        @touchstart.prevent="handleCrosshairTouchStart"
      >
        <div class="bg-red-500 text-white rounded-md px-2 py-1 shadow-lg flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-3" viewBox="0 0 12 16" fill="currentColor">
            <rect x="1" y="0" width="2" height="16" rx="1"/>
            <rect x="5" y="0" width="2" height="16" rx="1"/>
            <rect x="9" y="0" width="2" height="16" rx="1"/>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Charts List -->
    <div 
      ref="chartsContainer"
      class="flex-1 min-h-0 overflow-y-auto overscroll-y-contain pr-2 relative"
      @click="handleChartClick"
    >
      <!-- Crosshair Line - full height of charts list (scroll height) -->
      <div
        v-if="showCrosshair"
        ref="crosshairLineRef"
        class="absolute top-0 w-0.5 bg-red-500 opacity-60 z-40 pointer-events-none"
        :style="{ left: `${crosshairPosition}px`, height: `${crosshairHeight}px` }"
      ></div>
      
      <draggable
        v-if="chartsList && chartsList.length > 0"
        v-model="chartsList"
        :disabled="!editMode"
        ghost-class="drag-ghost"
        drag-class="dragging-item"
        :animation="200"
        :delay="150"
        :delay-on-touch-only="true"
        :scroll="chartsContainer"
        :scrollSensitivity="100"
        :scrollSpeed="20"
        :forceFallback="false"
        class="space-y-4"
        item-key="id"
      >
      <template #item="{ element: chart }">
        <div class="relative group transition-all duration-200" :class="editMode ? 'cursor-move' : ''">
          <!-- Edit mode drag overlay - covers the chart to enable full-surface dragging and block Plotly interactions -->
          <div
            v-if="editMode"
            class="absolute inset-0 z-20 cursor-move rounded-lg"
          ></div>

          <!-- Edit Mode Overlay - Top Right Corner -->
          <div 
            v-if="editMode" 
            class="absolute top-0 right-0 z-30 px-3 py-2 flex gap-1"
          >
            <button
              @click.stop="handleEditChart(chart.id)"
              class="p-1 hover:bg-blue-200 dark:hover:bg-blue-700 rounded transition-colors cursor-pointer"
              aria-label="Edit chart"
              title="Edit chart"
            >
              <PencilSquareIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </button>
            <button
              @click.stop="handleDeleteChart(chart.id)"
              class="p-1 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors cursor-pointer"
              aria-label="Delete chart"
              title="Delete chart"
            >
              <svg class="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <!-- Chart Widget -->
          <TimeseriesWidget
            :widget="chart"
            :dataID="dataID"
            :timeFormat="timeFormat"
            :streamData="streamData"
            :historicalData="historicalData"
            :historicalLoading="historicalLoading"
            :syncXAxis="syncedXAxis"
            :fullXAxisRange="fullXAxisRange"
            :crosshairShow="showCrosshair"
            :crosshairPosition="crosshairPosition"
            :crosshairContainerLeft="crosshairContainerLeft"
            :crosshairViewportX="crosshairViewportX"
            :isLiveMode="isLiveMode"
            :sourceErrors="sourceErrors"
            :editMode="editMode"
            @xAxisChange="handleXAxisChange"
          />
        </div>
      </template>
    </draggable>
    </div>

    <!-- Saving Indicator -->
    <div v-if="saving" class="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      Saving...
    </div>
  </div>
</template>

<style scoped>
/* Placeholder slot while dragging: invisible content, visible empty space */
:deep(.drag-ghost) {
  opacity: 0 !important;
  border: 2px dashed #6b7280;
  border-radius: 0.5rem;
  background: transparent !important;
  box-shadow: none !important;
}

/* The floating clone you drag around: fully opaque */
:deep(.dragging-item) {
  opacity: 1 !important;
}
</style>
